/**
 * HTTP insight service — gateway `/insights*` endpoints (proxied to the
 * intelligence-service under `/api/v1/insights`).
 *
 * Contract notes (verified against taxhive-intelligence-service
 * `app/api/v1/insight_routes.py`):
 *  - List is `GET /insights` with `page`/`page_size`/`status`/`insight_type`/
 *    `customer_id` query params, returning a paginated `{ items, total, … }`.
 *  - Per-customer is `GET /insights/customer/{id}` (also `{ items, … }`).
 *  - Dismiss/apply are `POST /insights/{id}/dismiss` | `/apply`, returning the
 *    updated insight.
 *  - Generate is `POST /insights/generate` with `{ customer_id, insight_types?,
 *    force_refresh }`, returning a generation summary (not the insights).
 *  - `customer_id` is numeric server-side; the mappers coerce the string id.
 */

import { apiClient, toServiceError } from '@/services/http/client'
import type { InsightService } from './insights.service'
import type {
  Insight,
  InsightListFilters,
  GenerateInsightsInput,
  GenerateInsightsResult,
} from '@/types/insight.types'
import {
  mapInsightFromApi,
  mapInsightListFromApi,
  mapGenerateToApi,
  mapGenerateResultFromApi,
} from './mappers'

const DEFAULT_PAGE_SIZE = 100

function buildListParams(filters?: InsightListFilters): Record<string, unknown> {
  const params: Record<string, unknown> = { page: 1, page_size: DEFAULT_PAGE_SIZE }
  if (filters?.status) params.status = filters.status
  if (filters?.insightType) params.insight_type = filters.insightType
  if (filters?.customerId) params.customer_id = Number(filters.customerId)
  return params
}

export function createHttpInsightService(): InsightService {
  return {
    async list(filters?: InsightListFilters): Promise<Insight[]> {
      try {
        const res = await apiClient.get('/insights', { params: buildListParams(filters) })
        return mapInsightListFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async listForCustomer(
      customerId: string,
      filters?: InsightListFilters
    ): Promise<Insight[]> {
      try {
        const params: Record<string, unknown> = {}
        if (filters?.status) params.status = filters.status
        if (filters?.insightType) params.insight_type = filters.insightType
        const res = await apiClient.get(`/insights/customer/${Number(customerId)}`, { params })
        return mapInsightListFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async dismiss(id: string): Promise<Insight> {
      try {
        const res = await apiClient.post(`/insights/${id}/dismiss`)
        return mapInsightFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async apply(id: string): Promise<Insight> {
      try {
        const res = await apiClient.post(`/insights/${id}/apply`)
        return mapInsightFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async generate(input: GenerateInsightsInput): Promise<GenerateInsightsResult> {
      try {
        const res = await apiClient.post('/insights/generate', mapGenerateToApi(input))
        return mapGenerateResultFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },
  }
}
